/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import { FormikTextField } from '../../formik-fields/formik-text-field';
import { RadioGroupItem } from '../../radio-group/radio-group-item';
import { useFormikContext } from 'formik';
import { Grid } from '@mui/material';
import { FormikSelectField } from '../../formik-fields/formik-select-field';
import { NumericListOptions } from '../../../models/interfaces/numeric-list-options';
import UserService from '../../../utilities/services/user-service';
import UserRoles from '../../../models/user-roles';
import { ListOptions } from '../../../models/interfaces/list-options';
import { useTranslation } from 'react-i18next';
import UserUtil from '../../../utilities/user-util';
import { IndustryListOptions } from '../../../models/industry';
import { PlanType } from '../../../models/enumerations/plan-type';

type DefaultFieldsProps = {
    organizationId?: string;
    accountType: RadioGroupItem,
    accountHolderName?: string,
    editing: boolean,
    initCustomTrialLength: boolean,
    loading: boolean,
};


export const DefaultFields: React.FC<DefaultFieldsProps> = (props: DefaultFieldsProps) => {
    const {
        accountHolderName,
        editing,
        loading,
        organizationId,
    } = props;
    const [holderOptions, setHolderOptions] = useState<ListOptions[]>([]);
    const [accountManagerSelectItems, setAccountManagerSelectItems] = useState<ListOptions[]>([]);

    const { t } = useTranslation();
    const formik = useFormikContext<any>();
    const { values, setFieldValue } = formik;

    useEffect(() => {
        const setHolders = async () => {
            const users = await UserService.getBy([
                {
                    field: 'organizationId',
                    operator: '==',
                    value: organizationId,
                },
                {
                    field: 'roles',
                    operator: '==',
                    value: [UserRoles.ADMIN_ID]
                }
            ]);


            const options: ListOptions[] = users.map((u) => {
                let name = u.email ?? `id: ${u.id}`;

                if (u.firstName && u.lastName) {
                    name = `${u.firstName} ${u.lastName}`;
                }

                if (u.firstName && !u.lastName) {
                    name = u.firstName;
                }

                if (!u.firstName && u.lastName) {
                    name = u.lastName;
                }

                const o: ListOptions = {
                    value: u.id!,
                    label: name,
                }

                return o;
            });


            // const finalOptions: ListOptions[] = [{ value: 'na', label: 'Select an account holder...' }];

            // options.forEach((opt) => finalOptions.push(opt));


            setHolderOptions(options);
        };
        if (accountHolderName === 'n/a' && editing) {
            setHolders();
        }
    }, [accountHolderName])

    // this is a hidden field value used only to determine whether or not to
    // validate account holder email when creating a new org.
    if (organizationId && !values['organizationId']) {
        setFieldValue('organizationId', organizationId)
    }

    useEffect(() => {
        if (accountHolderName) {
            setFieldValue('accountHolderName', accountHolderName);
        }
    }, [accountHolderName]);

    useEffect(() => {
        const fetchSuperAdmins = async () => {
            const superAdminsListOptions: ListOptions[] = [];
            const superAdmins = await UserService.getAllSuperAdmins();

            superAdmins.forEach((user) => {
                if (user.id && !UserUtil.isDeveloperUser(user)) {
                    const name = user ? `${(user.firstName || "")} ${(user.lastName || "")}` : "";

                    const selectItem: ListOptions = {
                        value: user.id,
                        label: name,
                    }

                    superAdminsListOptions.push(selectItem);
                }
            });

            setAccountManagerSelectItems(superAdminsListOptions);
        }

        fetchSuperAdmins();
    }, []);
    return (
        <>
            <Grid container rowSpacing={4} columnSpacing={3}>
                <Grid item xs={12}>
                    <FormikTextField
                        name="organizationName"
                        label={t('organization.edit.fields.orgName')}
                        disabled={loading}
                        required
                    />
                </Grid>
                {formik.values.plan?.value !== PlanType.External && (
                <Grid item xs={12}>
                    {
                        (accountHolderName === 'n/a' && editing) ? (
                            <FormikSelectField
                                label={'Account Holder'}
                                name={t('organization.edit.fields.accountHolder')}
                                disabled={loading}
                                items={holderOptions}
                                includeNone
                            />
                        ) : (

                            <FormikTextField
                                name={editing ? 'accountHolderName' : 'accountHolderEmail'}
                                label={editing ? t('organization.edit.fields.accountHolder') : t('organization.edit.fields.accountHolderEmail')}
                                disabled={loading}
                                readOnly={editing}
                                required={!editing}
                            />
                        )
                    }
                </Grid>
                )}
                <Grid item xs={12}>
                    <FormikSelectField
                        label={t('organization.edit.fields.accountManager')}
                        name="accountManager"
                        items={accountManagerSelectItems}
                        includeNone
                        disabled={loading}
                        onChange={(option: NumericListOptions) => setFieldValue('accountManager', option)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormikSelectField
                        label={t('organization.edit.fields.industry')}
                        name="industry"
                        items={IndustryListOptions}
                        includeNone
                        disabled={loading}
                        onChange={(option: ListOptions) => setFieldValue('industry', option)}
                    />
                </Grid>
                {formik.values.plan?.value !== PlanType.External && (
                    <Grid item xs={12}>
                        <FormikTextField
                            name="webhookUrl"
                            label={t('organization.edit.fields.webhookUrl')}
                            disabled={loading}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
}